import React, { useEffect, useState } from 'react';
import { ROUTES } from '../../../types/global/routes.types';
import {
    Box,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Slider,
    Switch,
    Typography,
} from '@mui/material';
import BrandManageRolePermissionsCard from './BrandManageRolePermissionsCard';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBrandOpsSettings, updateBrandOpsSettings } from '../../../redux/brandOps/actions';
import type { IBrandOpsSettings } from '../../../redux/brandOps/types';
import { brandOpsSettingsSelector } from '../../../redux/brandOps/selectors';
import { effectiveOperatingCountryCodeSelector } from '../../../selectors/selectors';
import { showToastMessage, useDispatchPromise } from '../../../utils/AppUtils';
import { BRAND_LUXE_MUTED, BRAND_LUXE_PRIMARY, brandModalOutlinedInputSx } from '../brandDashboardTokens';
import {
    BrandManageGate,
    ManageGoldButton,
    ManagePageHeader,
    ManageSurface,
    MANAGE_PAGE_SX,
    useBrandManageGate,
} from './manageShared';

type Form = {
    require_brand_approval: boolean;
    notify_on_publish: boolean;
    default_currency: string;
    autonomy_level: 'approval_required' | 'supervised' | 'autonomous';
    role_permissions: Record<string, string[]> | null;
    ai_dm_auto_reply_enabled: boolean;
    ai_dm_reply_delay_seconds: number;
};

const toForm = (s: IBrandOpsSettings | null, countryCode?: string | null): Form => ({
    require_brand_approval: s?.require_brand_approval ?? true,
    notify_on_publish: s?.notify_on_publish ?? false,
    default_currency: (
        s?.default_currency || ((countryCode ?? '').toUpperCase() === 'NG' ? 'NGN' : 'USD')
    ).toUpperCase(),
    autonomy_level: s?.autonomy_level ?? 'approval_required',
    role_permissions: s?.role_permissions ?? null,
    ai_dm_auto_reply_enabled: s?.ai_dm_auto_reply_enabled ?? false,
    ai_dm_reply_delay_seconds: s?.ai_dm_reply_delay_seconds ?? 30,
});

const switchSx = {
    '& .MuiSwitch-switchBase.Mui-checked': { color: BRAND_LUXE_PRIMARY },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: BRAND_LUXE_PRIMARY },
};

export default function BrandManageSettingsPage() {
    const dispatch = useDispatch();
    const dispatchP = useDispatchPromise();
    const { brandId } = useBrandManageGate();
    const settings = useSelector(brandOpsSettingsSelector);
    const countryCode = useSelector(effectiveOperatingCountryCodeSelector);
    const [form, setForm] = useState<Form>(() => toForm(settings, countryCode));
    const [saving, setSaving] = useState(false);
    const [savingPerms, setSavingPerms] = useState(false);

    useEffect(() => {
        if (brandId) dispatch(fetchBrandOpsSettings({ brandId, force: true }));
    }, [dispatch, brandId]);

    useEffect(() => {
        setForm(toForm(settings, countryCode));
    }, [settings, countryCode]);

    const saveSettings = async () => {
        if (!brandId) return;
        setSaving(true);
        try {
            await dispatchP(
                updateBrandOpsSettings({
                    brandId,
                    body: {
                        require_brand_approval: form.require_brand_approval,
                        notify_on_publish: form.notify_on_publish,
                        default_currency: form.default_currency.trim().toUpperCase() || 'USD',
                        autonomy_level: form.autonomy_level,
                        ai_dm_auto_reply_enabled: form.ai_dm_auto_reply_enabled,
                        ai_dm_reply_delay_seconds: form.ai_dm_reply_delay_seconds,
                    },
                }),
            );
            showToastMessage('Settings saved', 'success');
        } catch (err: any) {
            showToastMessage(err?.message || 'Could not save settings', 'error');
        } finally {
            setSaving(false);
        }
    };

    const savePermissions = async () => {
        if (!brandId) return;
        setSavingPerms(true);
        try {
            await dispatchP(
                updateBrandOpsSettings({
                    brandId,
                    body: { role_permissions: form.role_permissions },
                }),
            );
            showToastMessage('Permissions saved', 'success');
        } catch (err: any) {
            showToastMessage(err?.message || 'Could not save permissions', 'error');
        } finally {
            setSavingPerms(false);
        }
    };

    const labelSx = { '& .MuiFormControlLabel-label': { color: '#fff', fontSize: 14 } };

    return (
        <BrandManageGate>
            <Box sx={MANAGE_PAGE_SX}>
                <ManagePageHeader
                    title="Settings"
                    subtitle="Approval, notifications, and contract defaults."
                    back={ROUTES.BRAND_MANAGE}
                />
                <ManageSurface>
                    <FormControlLabel
                        sx={labelSx}
                        control={
                            <Switch
                                sx={switchSx}
                                checked={form.require_brand_approval}
                                onChange={e => setForm(f => ({ ...f, require_brand_approval: e.target.checked }))}
                            />
                        }
                        label="Require brand approval before publishing"
                    />
                    <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 12, mb: 1.5, ml: 6 }}>
                        Manager posts wait in pending approval until a brand admin approves.
                    </Typography>
                    <FormControlLabel
                        sx={labelSx}
                        control={
                            <Switch
                                sx={switchSx}
                                checked={form.notify_on_publish}
                                onChange={e => setForm(f => ({ ...f, notify_on_publish: e.target.checked }))}
                            />
                        }
                        label="Notify me when a post is published"
                    />
                    <FormControl size="small" sx={{ ...brandModalOutlinedInputSx, mt: 2.5, mb: 2 }}>
                        <InputLabel sx={{ color: BRAND_LUXE_MUTED }}>Default contract currency</InputLabel>
                        <Select
                            label="Default contract currency"
                            value={form.default_currency}
                            onChange={e => setForm(f => ({ ...f, default_currency: e.target.value }))}
                            sx={{ color: '#fff' }}>
                            <MenuItem value="USD">USD — US Dollar</MenuItem>
                            <MenuItem value="NGN">NGN — Nigerian Naira</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ ...brandModalOutlinedInputSx, mb: 0.5 }}>
                        <InputLabel sx={{ color: BRAND_LUXE_MUTED }}>Manager publish autonomy</InputLabel>
                        <Select
                            label="Manager publish autonomy"
                            value={form.autonomy_level}
                            onChange={e =>
                                setForm(f => ({ ...f, autonomy_level: e.target.value as Form['autonomy_level'] }))
                            }
                            sx={{ color: '#fff' }}>
                            <MenuItem value="approval_required">
                                Approval required — brand must approve every post
                            </MenuItem>
                            <MenuItem value="supervised">
                                Supervised — manager schedules, brand approves before publish
                            </MenuItem>
                            <MenuItem value="autonomous">
                                Autonomous — manager can approve and publish directly
                            </MenuItem>
                        </Select>
                    </FormControl>
                    <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 12, mb: 2 }}>
                        {form.autonomy_level === 'autonomous'
                            ? 'Manager can approve and publish posts without per-post brand sign-off.'
                            : 'Posts created by the manager will wait for brand approval before they can be published.'}
                    </Typography>
                    {/* AI DM auto-reply */}
                    <Box
                        sx={{
                            mt: 2.5,
                            mb: 1,
                            p: 2,
                            borderRadius: 2,
                            border: '1px solid rgba(255,201,101,0.15)',
                            bgcolor: 'rgba(255,201,101,0.04)',
                        }}>
                        <FormControlLabel
                            sx={labelSx}
                            control={
                                <Switch
                                    sx={switchSx}
                                    checked={form.ai_dm_auto_reply_enabled}
                                    onChange={e => setForm(f => ({ ...f, ai_dm_auto_reply_enabled: e.target.checked }))}
                                />
                            }
                            label="AI auto-reply for Instagram DMs"
                        />
                        <Typography
                            sx={{
                                color: BRAND_LUXE_MUTED,
                                fontSize: 12,
                                mb: form.ai_dm_auto_reply_enabled ? 2 : 0,
                                ml: 6,
                            }}>
                            When no one replies within the delay window, the AI responds using your brand knowledge.
                        </Typography>
                        {form.ai_dm_auto_reply_enabled && (
                            <Box sx={{ ml: 6, mr: 2 }}>
                                <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 12, mb: 1 }}>
                                    Auto-reply after{' '}
                                    <Box component="span" sx={{ color: BRAND_LUXE_PRIMARY, fontWeight: 600 }}>
                                        {form.ai_dm_reply_delay_seconds < 60
                                            ? `${form.ai_dm_reply_delay_seconds}s`
                                            : form.ai_dm_reply_delay_seconds % 60 === 0
                                            ? `${form.ai_dm_reply_delay_seconds / 60}m`
                                            : `${Math.floor(form.ai_dm_reply_delay_seconds / 60)}m ${
                                                  form.ai_dm_reply_delay_seconds % 60
                                              }s`}
                                    </Box>{' '}
                                    of no response
                                </Typography>
                                <Slider
                                    min={30}
                                    max={3600}
                                    step={30}
                                    value={form.ai_dm_reply_delay_seconds}
                                    onChange={(_, v) =>
                                        setForm(f => ({ ...f, ai_dm_reply_delay_seconds: v as number }))
                                    }
                                    marks={[
                                        { value: 30, label: '30s' },
                                        { value: 300, label: '5m' },
                                        { value: 900, label: '15m' },
                                        { value: 1800, label: '30m' },
                                        { value: 3600, label: '1h' },
                                    ]}
                                    sx={{
                                        color: BRAND_LUXE_PRIMARY,
                                        '& .MuiSlider-markLabel': { color: BRAND_LUXE_MUTED, fontSize: 10 },
                                    }}
                                />
                            </Box>
                        )}
                    </Box>

                    <ManageGoldButton onClick={saveSettings} disabled={saving || !brandId}>
                        {saving ? <CircularProgress size={16} sx={{ color: '#1a1919' }} /> : 'Save settings'}
                    </ManageGoldButton>
                </ManageSurface>
                <BrandManageRolePermissionsCard
                    value={form.role_permissions}
                    onChange={rp => setForm(f => ({ ...f, role_permissions: rp }))}
                    onSave={savePermissions}
                    saving={savingPerms}
                />
            </Box>
        </BrandManageGate>
    );
}
